import {
    FaEnvelope,
    FaFileAlt,
    FaPen,
    FaPhone,
    FaQuestion,
    FaRoute,
    FaStickyNote,
    FaTrash,
    FaUsers
} from "react-icons/fa";
import { Button } from "../../../../Common/Components/BaseComponents/Button";
import { InteractionType } from "shared-types";
import { useLeadsStore } from "../../../../Stores/LeadAndCustomer/leadsStore";
import { leadApi } from '../../../../services/api';
import { ChartData, ChartDisplay } from "../../../../Common/Components/BaseComponents/Graph";
import { set } from "lodash";
import { useState } from "react";

const getInteractionIcon = (type: InteractionType) => {
    switch (type) {
        case InteractionType.PHONE:
            return <FaPhone className="text-blue-500"/>;
        case InteractionType.EMAIL:
            return <FaEnvelope className="text-green-500"/>;
        case InteractionType.MEETING:
            return <FaUsers className="text-purple-500"/>;
        case InteractionType.TOUR:
            return <FaRoute className="text-orange-500"/>;
        case InteractionType.NOTE:
            return <FaStickyNote className="text-yellow-500"/>;
        case InteractionType.DOCUMENT:
            return <FaFileAlt className="text-red-500"/>;
        default:
            return <FaQuestion className="text-gray-400"/>;
    }
};

interface LeadInteractionDetailsProps {
    hideHeader?: boolean;
}

export const LeadInteractionDetails = ({ hideHeader = false }: LeadInteractionDetailsProps) => {
    const selectedLead = useLeadsStore(state => state.selectedLead);
    const { handleDeleteInteraction, resetSelectedLead, updateSelectedLead } = useLeadsStore();
    const isEditModalOpen = useLeadsStore(state => state.isEditModalOpen);
    const setIsEditModalOpen = useLeadsStore(state => state.setIsEditModalOpen);
    const editingInteraction = useLeadsStore(state => state.editingInteraction);
    const setEditingInteraction = useLeadsStore(state => state.setEditingInteraction);

    // Add modal state
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newInteraction, setNewInteraction] = useState({
        type: 'PHONE' as InteractionType,
        date: new Date().toISOString().split('T')[0],
        notes: ''
    });
    // סוגי אינטראקציה
    const interactionTypes = [
        { value: InteractionType.PHONE, label: "טלפון" },
        { value: InteractionType.EMAIL, label: "אימייל" },
        { value: InteractionType.MEETING, label: "פגישה" },
        { value: InteractionType.TOUR, label: "סיור" },
        { value: InteractionType.NOTE, label: "הערה" },
        { value: InteractionType.DOCUMENT, label: "מסמך" }
    ];

    const showGraphForId = useLeadsStore(state => state.showGraphForId);
    const setShowGraphForId = useLeadsStore(state => state.setShowGraphForId);
    const showGraph = selectedLead?.id === showGraphForId;

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingInteraction(null);
    };

    const closeAddModal = () => {
        setIsAddModalOpen(false);
        setNewInteraction({
            type: 'PHONE' as InteractionType,
            date: new Date().toISOString().split('T')[0],
            notes: ''
        });
    };

    const handleAddInteraction = async () => {
        if (!selectedLead?.id || !selectedLead?.email) {
            console.error('Selected lead missing required data');
            return;
        }

        try {
            const interactionToAdd = {
                ...newInteraction,
                userEmail: selectedLead.email,
                leadId: selectedLead.id,
                userId: selectedLead.id, // You might need to get actual user ID
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            // Add to server
            const response = await leadApi.addInteraction(selectedLead.id, interactionToAdd);
            console.log("🔍 Add interaction response:", response);

            // Update local state - use the proper ID from server response
            const interactionWithId = {
                ...interactionToAdd,
                id: response.id,
                createdAt: response.created_at || new Date().toISOString(),
                updatedAt: response.updated_at || new Date().toISOString()
            };

            const updatedInteractions = [
                ...(selectedLead.interactions || []),
                interactionWithId
            ];
            const updatedLead = { ...selectedLead, interactions: updatedInteractions };
            updateSelectedLead(updatedLead);

            closeAddModal();
            console.log("✅ Interaction added successfully");
        } catch (error) {
            console.error("Error adding interaction:", error);
        }
    };
    // פונקציה לשליחת עדכון האינטראקציה
    const handleSaveInteraction = async () => {
        if (editingInteraction && selectedLead?.id) {
            try {
                await leadApi.updateInteraction(editingInteraction.id!, editingInteraction);
                console.log("Interaction updated successfully");
                setIsEditModalOpen(false); // סגירת הפופאפ

                // עדכון האינטראקציה במצב המקומי במקום לבצע קריאה נוספת לשרת
                if (selectedLead?.interactions) {
                    const updatedInteractions = selectedLead.interactions.map(interaction =>
                        interaction.id === editingInteraction.id ? editingInteraction : interaction
                    );
                    // עדכון הליד הנבחר עם האינטראקציות המעודכנות
                    const updatedLead = { ...selectedLead, interactions: updatedInteractions };
                    updateSelectedLead(updatedLead);
                    console.log("🔄 Updated interaction locally:", editingInteraction);
                }
            } catch (error) {
                console.error("Error updating interaction:", error);
            }
        }
    };

    const deleteInteraction = async (interactionId: string) => {
        await handleDeleteInteraction(interactionId);

        // עדכון הסטייט לאחר מחיקה
        if (selectedLead) {
            const updatedInteractions = selectedLead.interactions.filter(i => i.id !== interactionId);
            set(selectedLead, "interactions", updatedInteractions); // תיקון של קריאת set

        }
    };

    const generateChartData = (): ChartData[] => {
        if (!selectedLead?.interactions) return [];

        const grouped: Record<string, number> = {};

        selectedLead.interactions.forEach(interaction => {
            const date = new Date(interaction.updatedAt).toISOString().split("T")[0]; // YYYY-MM-DD
            grouped[date] = (grouped[date] || 0) + 1;
        });

        return Object.entries(grouped).map(([date, count]) => ({
            label: date,
            value: count,
            date: date,
        }));
    };
    console.log(generateChartData());

    return (
        <div className={ hideHeader ? "" : "bg-blue-50 mt-2 p-4 rounded-lg border border-blue-200" }>
            <div className="flex gap-2 mb-6">
                <Button
                    color="blue"
                    size="sm"
                    onClick={ (e) => {
                        e.stopPropagation();
                        setIsAddModalOpen(true);
                    } }
                >
                    הוספת אינטראקציה
                </Button>
                <Button
                    color="blue"
                    size="sm"
                    onClick={ (e) => {
                        e.stopPropagation();
                        if (!selectedLead?.id) return;
                        setShowGraphForId(showGraph ? null : selectedLead.id);
                    } }
                >
                    { showGraph ? "הצג רשימת אינטראקציות" : "הצג גרף אינטראקציות" }
                </Button>
                <Button
                    color="blue"
                    size="sm"
                    onClick={ () => {
                        resetSelectedLead();
                        console.log(selectedLead);
                    } }
                >
                    סגור
                </Button>
            </div>
            <div className="text-sm text-gray-700 mb-2">
                { !hideHeader && (
                    <div>
                        { selectedLead && selectedLead.interactions?.length > 0 ? (
                            <div>
                                <p className="font-semibold">
                                    ל{ selectedLead.name } יש { selectedLead.interactions.length } אינטראקציות
                                </p>
                            </div>
                        ) : (
                            <p>ל{ selectedLead?.name } אין אינטראקציות</p>
                        ) }
                    </div>
                ) }

                <div className="flex flex-wrap gap-4 mt-4">
                    {
                        showGraph ? (
                            <div className="w-full min-w-[300px]">
                                <ChartDisplay title="אינטרקציות" type="line"
                                              data={ generateChartData() }></ChartDisplay>
                            </div>
                        ) : selectedLead?.interactions?.map((interaction) => {
                            console.log(interaction);
                            return (
                                <div key={ interaction.id } className="p-4 border rounded-lg shadow-md bg-white w-64">
                                    <div className="flex items-center gap-2 font-semibold">
                                        <span>סוג אינטראקציה:</span> { getInteractionIcon(interaction.type) } { interactionTypes.find(t => t.value === interaction.type)?.label || interaction.type }
                                    </div>
                                    <p className="font-semibold">תאריך
                                        עדכון: { new Date(interaction.updatedAt).toLocaleDateString() }</p>
                                    { interaction.notes && (
                                        <p className="font-semibold">הערות: <span
                                            className="font-normal">{ interaction.notes }</span></p>
                                    ) }

                                    <div className="flex gap-2 mt-4">
                                        <button
                                            onClick={ (e) => {
                                                e.stopPropagation();
                                                interaction.id && deleteInteraction(interaction.id);
                                            } }
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <FaTrash/>
                                        </button>
                                        <button
                                            onClick={ async e => {
                                                e.stopPropagation();
                                                await setIsEditModalOpen(true); // פותח את הפופאפ
                                                await setEditingInteraction({ ...interaction }); // עותק חדש של האינטראקציה
                                                console.log(editingInteraction);

                                            } }
                                            className="text-yellow-500 hover:text-yellow-700"
                                        >
                                            <FaPen/>
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>

                {/* פופאפ לעריכת אינטראקציה */ }
                { isEditModalOpen && editingInteraction && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
                        onClick={ e => e.stopPropagation() }
                    >
                        <div
                            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 border border-blue-200">
                            {/* כפתור סגירה עגול בפינה */ }
                            <button
                                onClick={ closeEditModal }
                                className="absolute top-4 left-4 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-full w-9 h-9 flex items-center justify-center shadow transition"
                                aria-label="סגור"
                                type="button"
                            >
                                ×
                            </button>
                            <h2 className="text-2xl font-bold text-blue-700 mb-6 text-center">עריכת אינטראקציה</h2>
                            {/* שדות הטופס */ }
                            <form
                                onSubmit={ e => {
                                    e.preventDefault();
                                    handleSaveInteraction();
                                } }
                            >
                                {/* סוג אינטראקציה */ }
                                <label className="block mb-2 font-semibold text-blue-700">סוג אינטראקציה:</label>
                                <select
                                    value={ editingInteraction.type }
                                    onChange={ e => setEditingInteraction({
                                        ...editingInteraction,
                                        type: e.target.value as InteractionType
                                    }) }
                                    className="mb-4 p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                                >
                                    { interactionTypes.map((type) => (
                                        <option key={ type.value } value={ type.value }>
                                            { type.label }
                                        </option>
                                    )) }
                                </select>

                                {/* תאריך */ }
                                <label className="block mb-2 font-semibold text-blue-700">תאריך:</label>
                                <input
                                    type="date"
                                    value={ editingInteraction.updatedAt?.split('T')[0] }
                                    onChange={ e => setEditingInteraction({
                                        ...editingInteraction,
                                        updatedAt: e.target.value
                                    }) }
                                    className="mb-4 p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                                />

                                {/* הערות */ }
                                <label className="block mb-2 font-semibold text-blue-700">הערות:</label>
                                <textarea
                                    value={ editingInteraction.notes || '' }
                                    onChange={ e => setEditingInteraction({
                                        ...editingInteraction,
                                        notes: e.target.value
                                    }) }
                                    className="mb-6 p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                                    rows={ 3 }
                                />

                                <div className="flex justify-end gap-2 mt-8">
                                    <button
                                        type="button"
                                        onClick={ closeEditModal }
                                        className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition"
                                    >
                                        סגור
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition"
                                    >
                                        שמור
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) }

                {/* פופאפ להוספת אינטראקציה */ }
                { isAddModalOpen && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
                        onClick={ e => e.stopPropagation() }
                    >
                        <div
                            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 border border-blue-200">
                            {/* כפתור סגירה עגול בפינה */ }
                            <button
                                onClick={ closeAddModal }
                                className="absolute top-4 left-4 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-full w-9 h-9 flex items-center justify-center shadow transition"
                                aria-label="סגור"
                                type="button"
                            >
                                ×
                            </button>
                            <h2 className="text-2xl font-bold text-blue-700 mb-6 text-center">הוספת אינטראקציה</h2>
                            {/* שדות הטופס */ }
                            <form
                                onSubmit={ e => {
                                    e.preventDefault();
                                    handleAddInteraction();
                                } }
                            >
                                {/* סוג אינטראקציה */ }
                                <label className="block mb-2 font-semibold text-blue-700">סוג אינטראקציה:</label>
                                <select
                                    value={ newInteraction.type }
                                    onChange={ e => setNewInteraction({
                                        ...newInteraction,
                                        type: e.target.value as InteractionType
                                    }) }
                                    className="mb-4 p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                                >
                                    { interactionTypes.map((type) => (
                                        <option key={ type.value } value={ type.value }>
                                            { type.label }
                                        </option>
                                    )) }
                                </select>

                                {/* תאריך */ }
                                <label className="block mb-2 font-semibold text-blue-700">תאריך:</label>
                                <input
                                    type="date"
                                    value={ newInteraction.date }
                                    onChange={ e => setNewInteraction({
                                        ...newInteraction,
                                        date: e.target.value
                                    }) }
                                    className="mb-4 p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                                />

                                {/* הערות */ }
                                <label className="block mb-2 font-semibold text-blue-700">הערות:</label>
                                <textarea
                                    value={ newInteraction.notes }
                                    onChange={ e => setNewInteraction({
                                        ...newInteraction,
                                        notes: e.target.value
                                    }) }
                                    className="mb-6 p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                                    rows={ 3 }
                                />

                                {/* כפתורי שמירה וביטול */ }
                                <div className="flex justify-end gap-2 mt-8">
                                    <button
                                        type="button"
                                        onClick={ closeAddModal }
                                        className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition"
                                    >
                                        ביטול
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
                                    >
                                        שמור
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) }
            </div>
        </div>
    );
}
